import { faGripVertical } from "@fortawesome/pro-solid-svg-icons";
import { faExpand, faMinimize } from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { atom, useAtom, useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
} from "../../../hooks";
import { CardWithTabs } from "../../../ui/Card";
import { CoordinatesField } from "../../../ui/GenericFields";
import { useEventBus } from "../../../utils/eventBus";
import { useReverseGeocoding } from "../../../utils/hooks";
import {
    ClearFiltersButton,
    useActiveFilters,
} from "../../DataTable/ClearFiltersButton";
import type { FilterState, TableDateFilterState } from "../../DataTable/state";
import { EquipmentTree } from "../EquipmentTree";
import { DatePicker } from "./DatePicker";
import { PresetIds } from "./PresetIds";
import {
    emissionPageStateFamily,
    emissionsTableAtom,
    endOfUtcDay,
    selectionContextAtom,
    startOfUtcDay,
    tableStateToEmissionFilters,
} from "./state";
import { EmissionDetail } from "./Tabs/EmissionDetail";
import { EmissionsTable, emissionColumns } from "./Tabs/EmissionTable";
import { NonDetectsTable } from "./Tabs/NonDetectsTable";
import { SiteInfo } from "./Tabs/SiteInfo";
import { SiteMap } from "./Tabs/SiteMap";
import { Timeline } from "./Tabs/Timeline";

interface SiteViewProps {
    siteId: string;
}

export const SiteView = (props: SiteViewProps) => {
    const [expandedCard, setExpandedCard] = useState<
        "TIMELINE" | "INFO" | "MAP"
    >(null);

    // Instance emission table state early, it'll
    // contain filters synced page-wide
    const emissionTableAtom = useMemo(
        () => emissionsTableAtom(props.siteId),
        [props.siteId],
    );
    // Writable derived atom exposing just the table's filters slice.
    const filtersAtom = useMemo(
        () =>
            atom(
                (get) => get(emissionTableAtom).filters,
                (
                    get,
                    set,
                    update: FilterState | ((prev: FilterState) => FilterState),
                ) => {
                    set(emissionTableAtom, (prev) => ({
                        ...prev,
                        filters:
                            typeof update === "function"
                                ? update(prev.filters)
                                : update,
                    }));
                },
            ),
        [emissionTableAtom],
    );
    // Instance derived atom (cascades instances main atom and
    // retrieves values from storage).
    const [filterState, setFilterState] = useAtom<FilterState>(filtersAtom);

    // Emission filters in the EmissionFilters shape consumed by SiteMap/Timeline.
    const emissionFilters = useMemo(
        () => ({
            ...tableStateToEmissionFilters(filterState),
            relatedSite: props.siteId,
        }),
        [filterState, props.siteId],
    );

    // Day-aligned range, shared with the Timeline so every panel covers the
    // same days.
    const { dateFrom, dateTo } = useAtomValue(
        emissionPageStateFamily(props.siteId),
    );

    // Active filters for the shared clear-filters button. The date range is the
    // page's primary control (the DatePicker), not a removable filter chip, so
    // it is ignored here and preserved when clearing all.
    const activeFilters = useActiveFilters({
        columns: emissionColumns,
        filters: filterState,
        ignoreKeys: ["detectionDateRange"],
    });
    const clearAllFilters = () =>
        setFilterState((fs) => ({ detectionDateRange: fs.detectionDateRange }));
    const clearFilter = (key: string) =>
        setFilterState((fs) => ({ ...fs, [key]: undefined }));

    // Context selection
    const [selectionContext, setSelectionContext] =
        useAtom(selectionContextAtom);

    // Initial params on page load
    const [urlParams, setSearchParams] = useSearchParams();
    const emissionRecordsApiClient = useEmissionRecordsApiClient();
    const urlParamsProcessed = useRef(false);

    useEffect(() => {
        if (urlParamsProcessed.current) return;

        let selectedContext = urlParams.get("selectedContext");
        const dateAfter = urlParams.get("detectionDateRangeAfter");
        const dateBefore = urlParams.get("detectionDateRangeBefore");
        const eventForm = urlParams.get("eventForm");

        // Clear parameters from the URL
        if (selectedContext || dateAfter || dateBefore) {
            setSearchParams((prev) => {
                prev.delete("selectedContext");
                prev.delete("detectionDateRangeAfter");
                prev.delete("detectionDateRangeBefore");
                return prev;
            });
        }

        if (!selectedContext && eventForm) {
            selectedContext = eventForm;
        }
        if (!selectedContext) return;
        urlParamsProcessed.current = true;

        (async () => {
            const emission =
                await emissionRecordsApiClient.emissionRecordsRetrieve({
                    id: selectedContext,
                });

            const emissionTimestamp = DateTime.fromJSDate(
                emission.dataPoint.detectionTimestamp,
            );

            // Set date range: use URL params if provided, otherwise ±6 months from emission
            const parsedAfter = dateAfter ? DateTime.fromISO(dateAfter) : null;
            const parsedBefore = dateBefore
                ? DateTime.fromISO(dateBefore)
                : null;

            // REPLACES filter state with date range only.
            // This is to avoid providers filters from hiding the selected emission!
            if (parsedAfter?.isValid && parsedBefore?.isValid) {
                setFilterState({
                    detectionDateRange: {
                        type: "date",
                        after: startOfUtcDay(parsedAfter.toJSDate()),
                        before: endOfUtcDay(parsedBefore.toJSDate()),
                    },
                });
            } else {
                const after = startOfUtcDay(
                    emissionTimestamp.minus({ months: 6 }).toJSDate(),
                );
                const before = endOfUtcDay(
                    DateTime.max(
                        emissionTimestamp.plus({ months: 6 }),
                        DateTime.now(),
                    ).toJSDate(),
                );
                setFilterState({
                    detectionDateRange: {
                        type: "date",
                        after,
                        before,
                    },
                });
            }

            // Set selection context (same logic as emission click handler)
            setSelectionContext((s) => ({
                ...s,
                emissionId: selectedContext,
                dateRange: {
                    start: emissionTimestamp.minus({ days: 7 }).toJSDate(),
                    end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
                },
                highlightPosition: {
                    date: emissionTimestamp.toJSDate(),
                    x: 0,
                },
                focusedEmissionId: undefined,
                focusedPlumeId: undefined,
            }));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Connect event bus
    const requestCloseForm = useEventBus("sitePageEventFormClose");

    // If something tries to close the form OR set the selected emission
    // to something different than the currently open form then we
    // request the form to close. If it fails it'll restore the state.
    const eventForm = urlParams.get("eventForm");
    useEffect(() => {
        if (eventForm && selectionContext.emissionId !== eventForm) {
            requestCloseForm();
        }
    }, [selectionContext.emissionId, eventForm, requestCloseForm]);

    // Fetch site data
    const apiClient = useInfrastructureApiClient();
    const siteQuery = useQuery({
        queryKey: ["site", props.siteId],
        queryFn: async () => {
            const response = await apiClient.infrastructureSitesRetrieve({
                id: props.siteId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    // Reverse location of site
    const placeResult = useReverseGeocoding(
        siteQuery.data?.location.coordinates,
    );

    useDocumentTitle(`${siteQuery.data?.siteName ?? "Site"} - AERSHED`);

    return (
        <div className="flex-1 px-4 pt-2 pb-4 flex flex-col">
            <div className="@container flex justify-between mt-1 mb-2">
                <div className="block flex-1 min-w-0 items-center gap-4 @[900px]:flex">
                    <div className="h-fit">
                        <p className="text-2xl">
                            {siteQuery.data?.siteName || "Site"}
                        </p>
                        <p className="text-xs">
                            {placeResult.status === "DONE" &&
                                placeResult.location}
                        </p>
                    </div>
                    <hr className="hidden @[900px]:block h-10 border" />
                    <div className="text-xs flex-1 min-w-0">
                        {siteQuery.data && (
                            <PresetIds presetIds={siteQuery.data.presetIds} />
                        )}
                        <CoordinatesField
                            coordinates={siteQuery.data?.location.coordinates}
                            allowCopy
                            mapLinkParams={{
                                siteId: props.siteId,
                                lat: siteQuery.data?.location.coordinates[1].toString(),
                                lon: siteQuery.data?.location.coordinates[0].toString(),
                            }}
                        />
                    </div>
                </div>
                {/* Date controls and settings */}
                <div className="flex items-center justify-end gap-2">
                    <ClearFiltersButton
                        activeFilters={activeFilters}
                        onClearFilter={clearFilter}
                        onClearAll={clearAllFilters}
                    />
                    <DatePicker
                        after={
                            (
                                filterState.detectionDateRange as TableDateFilterState
                            )?.after
                        }
                        onChangeAfter={(newDate) => {
                            setFilterState((fs) => ({
                                ...fs,
                                detectionDateRange: {
                                    type: "date",
                                    after: startOfUtcDay(newDate),
                                    before: (
                                        fs.detectionDateRange as TableDateFilterState
                                    )?.before,
                                },
                            }));
                            setSelectionContext({});
                        }}
                        before={
                            (
                                filterState.detectionDateRange as TableDateFilterState
                            )?.before
                        }
                        onChangeBefore={(newDate) => {
                            setFilterState((fs) => ({
                                ...fs,
                                detectionDateRange: {
                                    type: "date",
                                    after: (
                                        fs.detectionDateRange as TableDateFilterState
                                    )?.after,
                                    before: endOfUtcDay(newDate),
                                },
                            }));
                            setSelectionContext({});
                        }}
                    />
                </div>
            </div>

            {/* Page content */}
            <div className="flex flex-col gap-2 flex-1">
                {/* Emission info */}
                {expandedCard !== "TIMELINE" && (
                    <div
                        className={`flex gap-2 ${
                            expandedCard === null
                                ? "min-h-[40vh] max-h-[40vh]"
                                : "flex-1 min-h-0"
                        }`}
                    >
                        {(expandedCard === null || expandedCard === "INFO") && (
                            <CardWithTabs
                                tabs={[
                                    {
                                        id: "emissions",
                                        label: "Emissions",
                                        icon: faGripVertical,
                                        children:
                                            selectionContext.emissionId ? (
                                                <EmissionDetail
                                                    emissionId={
                                                        selectionContext.emissionId
                                                    }
                                                    expandData={(id) =>
                                                        setSearchParams({
                                                            ...Object.fromEntries(
                                                                urlParams,
                                                            ),
                                                            eventForm: id,
                                                        })
                                                    }
                                                    onChangeEmission={(
                                                        emissionId,
                                                    ) => {
                                                        setSelectionContext({
                                                            emissionId,
                                                        });
                                                    }}
                                                />
                                            ) : (
                                                <EmissionsTable
                                                    siteId={props.siteId}
                                                    onClickEmission={(
                                                        selectedEmission,
                                                    ) => {
                                                        const emissionTimestamp =
                                                            DateTime.fromJSDate(
                                                                selectedEmission
                                                                    .dataPoint
                                                                    .detectionTimestamp,
                                                            );
                                                        // When selecting emission zoom in to +- 7 days of the emission.
                                                        setSelectionContext(
                                                            (s) => ({
                                                                ...s,
                                                                emissionId:
                                                                    selectedEmission.id,
                                                                dateRange: {
                                                                    start: emissionTimestamp
                                                                        .minus({
                                                                            days: 7,
                                                                        })
                                                                        .toJSDate(),
                                                                    end: emissionTimestamp
                                                                        .plus({
                                                                            days: 7,
                                                                        })
                                                                        .toJSDate(),
                                                                },
                                                                highlightPosition:
                                                                    {
                                                                        date: emissionTimestamp.toJSDate(),
                                                                        x: 0,
                                                                    },
                                                                // New anchor - drop
                                                                // the old focus.
                                                                focusedEmissionId:
                                                                    undefined,
                                                                focusedPlumeId:
                                                                    undefined,
                                                            }),
                                                        );
                                                    }}
                                                />
                                            ),
                                    },
                                    {
                                        id: "non-detects",
                                        label: "Non-detects",
                                        icon: faGripVertical,
                                        children: (
                                            <NonDetectsTable
                                                siteId={props.siteId}
                                                dateFrom={dateFrom}
                                                dateTo={dateTo}
                                            />
                                        ),
                                    },
                                    {
                                        id: "equipmentTree",
                                        label: "Equipment",
                                        icon: faGripVertical,
                                        children: (
                                            <EquipmentTree
                                                siteId={props.siteId}
                                            />
                                        ),
                                    },
                                    {
                                        id: "site-attributes",
                                        label: "Site Attributes",
                                        icon: faGripVertical,
                                        children: (
                                            <SiteInfo site={siteQuery.data} />
                                        ),
                                    },
                                ]}
                                extraActions={[
                                    {
                                        title: "Expand/collapse",
                                        icon:
                                            expandedCard === "INFO"
                                                ? faMinimize
                                                : faExpand,
                                        action: () =>
                                            setExpandedCard((s) =>
                                                s === "INFO" ? null : "INFO",
                                            ),
                                    },
                                ]}
                            />
                        )}

                        {(expandedCard === null || expandedCard === "MAP") && (
                            <CardWithTabs
                                tabs={[
                                    {
                                        id: "map",
                                        label: "Map",
                                        icon: faGripVertical,
                                        children: (
                                            <SiteMap
                                                siteId={props.siteId}
                                                dateFrom={dateFrom}
                                                dateTo={dateTo}
                                                filters={emissionFilters}
                                            />
                                        ),
                                    },
                                ]}
                                extraActions={[
                                    {
                                        title: "Expand/collapse",
                                        icon:
                                            expandedCard === "MAP"
                                                ? faMinimize
                                                : faExpand,
                                        action: () =>
                                            setExpandedCard((s) =>
                                                s === "MAP" ? null : "MAP",
                                            ),
                                    },
                                ]}
                            />
                        )}
                    </div>
                )}

                {/* Timeline view */}

                {(expandedCard === null || expandedCard === "TIMELINE") && (
                    <CardWithTabs
                        tabs={[
                            {
                                id: "timeline",
                                label: "Timeline",
                                icon: faGripVertical,
                                children: <Timeline siteId={props.siteId} />,
                            },
                        ]}
                        extraActions={[
                            {
                                title: "Expand/collapse",
                                icon:
                                    expandedCard === "TIMELINE"
                                        ? faMinimize
                                        : faExpand,
                                action: () =>
                                    setExpandedCard((s) =>
                                        s === "TIMELINE" ? null : "TIMELINE",
                                    ),
                            },
                        ]}
                    />
                )}

                {/* Extra space at the bottom for scrolling*/}
                <div className="h-32"></div>
            </div>
        </div>
    );
};
